import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useClients } from '../hooks/useClients';
import { useAgents } from '../hooks/useAgents';
import { useCalendar } from '../hooks/useCalendar';
import { usePosts } from '../hooks/usePosts';
import { useApprovals } from '../hooks/useApprovals';
import { usePermissions } from '../hooks/usePermissions';
import ClientCard from '../components/ClientCard';
import CalendarWidget from '../components/CalendarWidget';
import AgentCard from '../components/AgentCard';
import Modal from '../components/shared/Modal';
import ClientForm from '../components/forms/ClientForm';
import PostStatusBadge from '../components/forms/PostStatusBadge';

function Overview() {
  const { clients, loading, error, createClient, refetch } = useClients();
  const { agents } = useAgents();
  const { upcomingPosts, allPosts } = useCalendar();
  const { updatePostStatus } = usePosts({ onUpdate: refetch });
  const { approvals } = useApprovals();
  const { isAdmin, isCliente } = usePermissions();
  const [showNewClient, setShowNewClient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const handleCreateClient = async (data) => {
    setSaving(true);
    try {
      await createClient(data);
      setShowNewClient(false);
      setToast({ type: 'success', message: `Cliente "${data.name}" criado com sucesso` });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setToast({ type: 'error', message: `Erro: ${err.message}` });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  // Posts que precisam de acao (rejeitados ou revisao solicitada)
  const actionNeededPosts = [];
  if (!isCliente) {
    for (const client of clients) {
      for (const post of client.posts) {
        if (post.status === 'revisao_solicitada' || post.status === 'rejeitado') {
          // Buscar o feedback mais recente do cliente para este post
          const postApprovals = approvals
            .filter(a => a.postId === post.id)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          const latestFeedback = postApprovals[0] || null;

          actionNeededPosts.push({
            ...post,
            clientName: client.name,
            clientId: client.id,
            feedback: latestFeedback?.notes || '',
            feedbackBy: latestFeedback?.reviewerName || '',
            feedbackAction: latestFeedback?.action || '',
            feedbackDate: latestFeedback?.createdAt || '',
          });
        }
      }
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        Carregando dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">&#x26A0;</div>
        <p>Erro ao carregar dados: {error}</p>
        <button className="btn btn-primary" style={{ marginTop: 'var(--spacing-md)' }} onClick={refetch}>
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 className="page-title">SOEVAN Agency</h1>
            <p className="page-subtitle">
              {clients.length} cliente{clients.length !== 1 ? 's' : ''} &middot; {agents.length} agentes &middot; {allPosts.length} posts agendados
            </p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowNewClient(true)}>
              + Novo Cliente
            </button>
          )}
        </div>
      </div>

      {/* Posts aguardando acao */}
      {actionNeededPosts.length > 0 && (
        <div className="section">
          <h2 className="section-title" style={{ color: '#f97316' }}>
            &#x26A0; Posts Aguardando Acao ({actionNeededPosts.length})
          </h2>
          <div className="action-needed-list">
            {actionNeededPosts.map(post => (
              <Link
                key={post.id}
                to={`/client/${post.clientId}`}
                className="action-needed-card"
                style={{ textDecoration: 'none' }}
              >
                <div className="action-needed-header">
                  <div>
                    <div className="action-needed-title">{post.workingTitle || post.title}</div>
                    <div className="action-needed-meta">
                      {post.clientName} &middot; {post.format} &middot; {post.date}
                    </div>
                  </div>
                  <PostStatusBadge status={post.status} />
                </div>
                {post.feedback && (
                  <div className="action-needed-feedback">
                    <div className="action-needed-feedback-label">
                      Feedback de {post.feedbackBy}:
                    </div>
                    <div className="action-needed-feedback-text">
                      "{post.feedback}"
                    </div>
                  </div>
                )}
                {!post.feedback && (
                  <div className="action-needed-feedback">
                    <div className="action-needed-feedback-text" style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                      Nenhum feedback fornecido pelo cliente.
                    </div>
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Clientes */}
      <div className="section">
        <h2 className="section-title">Clientes</h2>
        <div className="grid grid-3">
          {clients.map(client => (
            <ClientCard key={client.id} client={client} />
          ))}
        </div>
      </div>

      {/* Proximos Posts */}
      <div className="section">
        <h2 className="section-title">Proximos Posts</h2>
        <div className="card">
          <CalendarWidget posts={upcomingPosts} showClient compact onChangeStatus={updatePostStatus} />
        </div>
      </div>

      {/* Atividade Recente — Agentes */}
      {!isCliente && (
        <div className="section">
          <h2 className="section-title">Equipe ({agents.length} agentes)</h2>
          <div className="grid grid-4">
            {agents.slice(0, 8).map(agent => (
              <AgentCard key={agent.id} agent={agent} showSquad />
            ))}
          </div>
        </div>
      )}

      {/* Modal novo cliente */}
      {showNewClient && (
        <Modal title="Novo Cliente" onClose={() => setShowNewClient(false)}>
          <ClientForm
            onSubmit={handleCreateClient}
            onCancel={() => setShowNewClient(false)}
            saving={saving}
          />
        </Modal>
      )}

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast toast--${toast.type}`}>{toast.message}</div>
        </div>
      )}
    </div>
  );
}

export default Overview;
